import { useState, useEffect, useMemo } from "react";
import { Box, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid } from "@mui/material";
import PropTypes from "prop-types";

import SoftBox from "components/SoftComponents/SoftBox";
import SoftButton from "components/SoftComponents/SoftButton";
import SoftInput from "components/SoftComponents/SoftInput";
import SoftTypography from "components/SoftComponents/SoftTypography";
import SoftSelect from "components/SoftComponents/SoftSelect";
import moment from "moment-timezone";

const optionBoxStyles = (isChecked, paletteColour, badgeColors = {}) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    border: '1px solid',
    borderColor: isChecked ? badgeColors?.[paletteColour]?.text : 'grey.300',
    backgroundColor: isChecked ? badgeColors?.[paletteColour]?.background : 'grey.100',
    borderRadius: '8px',
    px: 1.5,
    py: 1,
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    userSelect: 'none',
    height: '100%',
    width: '100%',
    boxSizing: 'border-box',
    '&:hover': {
        backgroundColor: isChecked ? 'rgba(33, 150, 243, 0.12)' : 'rgba(0, 0, 0, 0.03)',
    }
});

const ToggleBox = ({ isChecked, label, onClick, disabled, paletteColor = 'info' }) => (
    <Box
        sx={({ palette: { badgeColors } }) => (optionBoxStyles(isChecked, paletteColor, badgeColors))}
        onClick={!disabled ? onClick : undefined}
    >
        <SoftBox display="flex" alignItems="center" gap={1.5}>
            <SoftTypography variant="button" fontSize="13px" fontWeight={isChecked ? "bold" : "regular"} textTransform="capitalize">
                {label.split("_").join(" ")}
            </SoftTypography>
        </SoftBox>
    </Box>
);

function EditInspectionDialog({ user, selectedInspection, maintenance_tags, isEditModalOpen, setIsEditModalOpen, saveEditedInspection }) {

    const [formData, setFormData] = useState({
        maintenanceTag: "",
        recordedAt: "",
        priority: "",
        status: "",
        conditions: {},
        actions: {}
    });

    const maintenanceTagOptions = useMemo(() => {
        if (!maintenance_tags?.data) return [];
        return maintenance_tags.data.map(m => ({ label: m.tag_label, value: m.tag_label }));
    }, [maintenance_tags]);

    const priorityOptions = [
        { value: "low", label: "Low" },
        { value: "medium", label: "Medium" },
        { value: "high", label: "High" }
    ];

    const createAttributes = (devices, attrType) => {
        let ret = {};
        for (let dev of devices) {
            if (typeof ret[dev.uuid] === 'undefined') ret[dev.uuid] = dev[attrType] || {};
        }
        return ret;
    }

    useEffect(() => {
        if (selectedInspection) {
            setFormData({
                maintenanceTag: selectedInspection?.maintenance_tag?.tag_label || null,
                recordedAt: moment.tz(selectedInspection?.recordedAt, user?.organisations?.find(row => row.organisation_id == selectedOrg)?.timezone || moment.tz.guess()).format('YYYY-MM-DDTHH:mm') || null,
                priority: selectedInspection?.priority || null,
                status: selectedInspection.status || null,
                conditions: selectedInspection.devices ? createAttributes(selectedInspection.devices, 'conditions') : {},
                actions: selectedInspection.devices ? createAttributes(selectedInspection.devices, 'actions') : {}
            });
        }
    }, [selectedInspection]);

    const handleFormChange = (selected) => {
        const { name, value } = selected;
        setFormData((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleConditionChange = (uuid, key, value) => {
        setFormData(prev => ({
            ...prev,
            conditions: {
                ...prev.conditions,
                [uuid]: {
                    ...prev.conditions[uuid],
                    [key]: value
                }
            }
        }));
    };

    const handleActionChange = (uuid, key, value) => {
        setFormData(prev => ({
            ...prev,
            actions: {
                ...prev.actions,
                [uuid]: {
                    ...prev.actions[uuid],
                    [key]: value
                }
            }
        }));
    };

    const handleSaveInspection = (e) => {
        e.preventDefault();

        var apiPayload = new FormData();

        const payload = {
            metadata: {
                ...selectedInspection
            },
            newdata: {
                maintenance_tag: maintenance_tags?.data?.find(tag => tag.tag_label === formData.maintenanceTag)?.tag_label || null,
                recordedAt: new Date(formData.recordedAt).toISOString(),
                priority: formData.priority,
                status: formData.status,
                conditions: formData.conditions,
                actions: formData.actions
            }
        };

        saveEditedInspection.mutate([payload, apiPayload]);
    };

    const isPending = saveEditedInspection.isPending;

    return (
        <Dialog open={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} fullWidth maxWidth="lg" PaperProps={{ sx: { borderRadius: '1rem', minHeight: 'fit-content' } }}>
            <DialogTitle>
                <SoftTypography variant="h6" fontWeight="bold">
                    Edit Inspection
                </SoftTypography>
            </DialogTitle>
            <DialogContent>
                <SoftBox component="form" onSubmit={handleSaveInspection} style={{ height: '100%' }}>
                    <SoftBox display="flex" flexDirection={{ xxs: 'column' }} gap={3}>
                        <Grid container spacing={2}>
                            <Grid item xxs={12} lg={4}>
                                <SoftTypography variant="caption" fontWeight="bold" mb={1} display="block">Maintenance Tag</SoftTypography>
                                <SoftSelect
                                    name="maintenanceTag"
                                    value={maintenanceTagOptions.find(opt => opt.value === formData.maintenanceTag) || null}
                                    onChange={(e) => handleFormChange({ name: 'maintenanceTag', ...e })}
                                    options={maintenanceTagOptions}
                                    disabled={isPending}
                                    isClearable={true}
                                />
                            </Grid>
                            <Grid item xxs={12} lg={4}>
                                <SoftTypography variant="caption" fontWeight="bold" mb={1} display="block">Priority</SoftTypography>
                                <SoftSelect
                                    name="priority"
                                    value={priorityOptions.find(opt => opt.value === formData.priority) || null}
                                    onChange={(e) => handleFormChange({ name: 'priority', ...e })}
                                    options={priorityOptions}
                                    disabled={isPending}
                                />
                            </Grid>
                            <Grid item xxs={12} lg={4}>
                                <SoftTypography variant="caption" fontWeight="bold" mb={1} display="block">Recorded Date</SoftTypography>
                                <SoftInput
                                    name="recordedAt"
                                    type="datetime-local"
                                    value={formData.recordedAt}
                                    onChange={(e) => handleFormChange({ name: 'recordedAt', value: e.target.value })}
                                    disabled={isPending}
                                />
                            </Grid>
                            <Grid item xxs={12}>
                                <SoftTypography variant="caption" fontWeight="bold" mb={1} display="block">Status</SoftTypography>
                                <SoftBox display="flex" flexDirection="row" gap={1.5}>
                                    <ToggleBox isChecked={formData.status === "resolved"} label="Resolved" paletteColor="success" disabled={isPending} onClick={() => handleFormChange({ name: "status", value: 'resolved' })} />
                                    <ToggleBox isChecked={formData.status === "review"} label="Review" paletteColor="warning" disabled={isPending} onClick={() => handleFormChange({ name: "status", value: 'review' })} />
                                    <ToggleBox isChecked={formData.status === "unresolved"} label="Unresolved" paletteColor="error" disabled={isPending} onClick={() => handleFormChange({ name: "status", value: 'unresolved' })} />
                                </SoftBox>
                            </Grid>
                        </Grid>
                        <Divider />
                        <SoftBox>
                            <SoftTypography variant="h6" fontWeight="bold" mb={2} display="block">
                                Device Attributes
                            </SoftTypography>
                            {selectedInspection?.devices?.map((dev, devIndex) => (
                                <SoftBox key={dev.uuid} mb={4}>
                                    <SoftTypography variant="subtitle3" fontWeight="bold" color="dark">
                                        {dev.name} ({dev.serial_no})
                                    </SoftTypography>

                                    <Grid container spacing={4}>
                                        <Grid item xxs={12} md={6}>
                                            <SoftTypography variant="caption" fontWeight="bold" mb={1} display="block">Conditions</SoftTypography>
                                            <Grid container spacing={1}>
                                                {Object.entries(formData.conditions[dev.uuid] || {}).map(([key, value]) => {
                                                    if (key === 'other') {
                                                        return (
                                                            <Grid item xxs={12} key={key}>
                                                                <SoftInput
                                                                    placeholder="Other condition..."
                                                                    value={value}
                                                                    onChange={(e) => handleConditionChange(dev.uuid, key, e.target.value)}
                                                                    disabled={isPending}
                                                                />
                                                            </Grid>
                                                        );
                                                    }
                                                    return (
                                                        <Grid item xxs={6} lg={4} key={key}>
                                                            <ToggleBox
                                                                isChecked={!!value}
                                                                label={key}
                                                                disabled={isPending}
                                                                onClick={() => handleConditionChange(dev.uuid, key, !value)}
                                                            />
                                                        </Grid>
                                                    );
                                                })}
                                            </Grid>
                                        </Grid>
                                        <Grid item xxs={12} md={6}>
                                            <SoftTypography variant="caption" fontWeight="bold" mb={1} display="block">Actions</SoftTypography>
                                            <Grid container spacing={1}>
                                                {Object.entries(formData.actions[dev.uuid] || {}).map(([key, value]) => {
                                                    if (key === 'other') {
                                                        return (
                                                            <Grid item xxs={12} key={key}>
                                                                <SoftInput
                                                                    placeholder="Other action..."
                                                                    value={value}
                                                                    onChange={(e) => handleActionChange(dev.uuid, key, e.target.value)}
                                                                    disabled={isPending}
                                                                />
                                                            </Grid>
                                                        );
                                                    }
                                                    return (
                                                        <Grid item xxs={6} lg={4} key={key}>
                                                            <ToggleBox
                                                                isChecked={!!value}
                                                                label={key}
                                                                disabled={isPending}
                                                                onClick={() => handleActionChange(dev.uuid, key, !value)}
                                                            />
                                                        </Grid>
                                                    );
                                                })}
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    {devIndex < selectedInspection.devices.length - 1 && <Divider sx={{ mt: 3 }} />}
                                </SoftBox>
                            ))}
                        </SoftBox>
                    </SoftBox>

                    <button type="submit" style={{ display: "none" }} />
                </SoftBox>
            </DialogContent>
            <DialogActions sx={{ p: 2, pt: 0 }}>
                <SoftButton onClick={() => setIsEditModalOpen(false)} variant="text" color="secondary" disabled={isPending}>
                    Cancel
                </SoftButton>
                <SoftButton onClick={handleSaveInspection} variant="gradient" color="dark" disabled={isPending}>
                    {isPending ? <CircularProgress size={20} color="inherit" /> : 'Save'}
                </SoftButton>
            </DialogActions>
        </Dialog>
    );
}

EditInspectionDialog.propTypes = {
    selectedInspection: PropTypes.object,
    maintenance_tags: PropTypes.object,
    isEditModalOpen: PropTypes.bool,
    setIsEditModalOpen: PropTypes.func,
    saveEditedInspection: PropTypes.object
};

export default EditInspectionDialog;